/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../nls/StringResources',
	'../../../lib/@waca/core-client/js/core-client/ui/core/View',
	'react-dom',
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'./components/SortableList'
], function (_, resources, View, ReactDOM, React, UI_CARBON_TOOLKIT, SortableList) {
	'use strict';

	/* eslint react/prop-types: 0 */ // TODO: Remove once we have prop-types brought in from glass.
	const { Dialog,
		Checkbox,
		TruncatedText,
		ListItem,
		Container,
		List,
		FlexLayout,
		FlexItem,
		SearchInput,
		Label,
		Select,
		Link,
		ProgressIndicator,
		Tooltip,
		EmptyState } = UI_CARBON_TOOLKIT;

	//functional component to render which ever error type is passed to it
	const RenderEmptyState = ({ type, title }) => (
		<div className='emptyState'>
			{<EmptyState type={ type } />}
			<div className='emptyStateTitle'>{ title }</div>
		</div>
	);

	class ReactComponents extends React.Component {

		constructor(props) {
			super(props);
			this.state = {
				...this.props,
				listData: [],
				visibleListData: [],
				selectAllValues: false,
				btnDisabled: true,
				sortableData: [],
				isLoading: true,
				isError: false,
				isSearchEmpty: false,
				previousSortableData: [],
				isClean: true
			};
			this.dialogProps = {
				ariaLabel: 'Dialog',
				className: 'customSortContainer',
				size: 'large',
				width: '640px',
				height: '699px',
				clickaway: false
			};
			this.sortRemainingItemsBy =  this.props.sortType;
			this.initialSortType = this.props.sortType;
		}

		componentDidMount() {
			return this.props.getItems()
				.then(dataItems => {
					//create an array of objects to be used by the itemRenderer function, and also filter out empty values, undefines, and nulls.
					let listData = dataItems
						.map(dataItem => this._getListItemFromQueryItem(dataItem))
						.filter(data => !!(_.isNumber(data.label) || data.label && data.label.trim()));
					const incomingTopValues = (this.props.sortSpec && this.props.sortSpec.custom && this.props.sortSpec.custom.top) || [];
					return this._getMissingListData(incomingTopValues, listData)
						.then((missingListData) => {
							listData = listData.concat(missingListData);
							this.setState({
								listData: listData,
								visibleListData: listData.slice(),
								isLoading: false
							}, () => {
								incomingTopValues.forEach(incomingTopValue => {
									this._columnSelected(incomingTopValue, true);
								});
							});
						});
				}, (e) => {
					this.props.logger.error(e);
					this.setState({
						isError: true
					});
				});
		}

		/**
		 * Returns a promise resolving to the missing items - may be empty array if none are missing
		 * @param {array} incomingValues an array of string use values
		 * @param {array} listData an array of list items
		 */
		_getMissingListData(incomingValues, listData) {
			let missingValues = incomingValues.filter(incomingValue => !listData.find(dataItem => dataItem.value === incomingValue));
			if (missingValues.length) {
				return this.props.getItemsFromValues(missingValues)
					.then(missingItems => {
						return (missingItems.map(missingItem => this._getListItemFromQueryItem(missingItem)));
					});
			} else {
				return Promise.resolve([]);
			}
		}

		/**
		 * Returns the list item from a raw query response item
		 * @param {object} queryItem
		 */
		_getListItemFromQueryItem(queryItem) {
			return {
				value: queryItem.value,
				label: queryItem.label,
				selected: false
			};
		}

		/**
		 * Returns true if the all the visible values in the item list are selected
		 * @returns {boolean}
		 */
		_updateSelectAllValues() {
			this.setState({
				selectAllValues: this.state.visibleListData.length && !this.state.visibleListData.find(dataItem => !dataItem.selected)
			});
		}


		/**
		 * Returns false iff sortableData is not empty and does not equal previousSortableData AND
		 * sortRemainingItemsBy does not equal initialSortType.
		 * @returns {boolean}
		 */
		_isClean(current=this.state.sortableData, previous=this.state.previousSortableData) {
			if (Array.isArray(current) && Array.isArray(previous) && current.length > 0) {
				if ( current.length !== previous.length ) { return false; }
				return JSON.stringify(current) === JSON.stringify(previous) && this.sortRemainingItemsBy === this.initialSortType;
			}
			return true;
		}

		/**
		 * Stops the propagation of an event and applies the current custom sort specification
		 * @param {object} evt
		 */
		_onApply(evt) {
			if (evt) {
				evt.stopPropagation();
			}

			this.props.applyCustomSort(this.state, this.sortRemainingItemsBy);
		}

		/**
		 * Stops the propagation of an event and removes the dialog
		 * @param {object} evt
		 */
		_onClose(evt) {
			if (evt) {
				evt.stopPropagation();
			}
			this.props.onRemove();
		}

		/**
		 * Sets the sort type and sets isClean state to determine if apply button is enabled.
		 * @param {string} sortType
		 */
		_onSelect(sortType) {
			this.sortRemainingItemsBy = sortType;
			this.setState({
				isClean: this._isClean()
			});
		}

		/**
		 * Performs the search for items matching the given string. Returning all if no string is specified.
		 * @param {string} value
		 */
		_onSearch(value) {
			value = value && value.trim();
			this.setState({
				isLoading: true,
				searchTerm: value,
				selectAllValues: false,
				isSearchEmpty: false
			});
			// new search - ensure all items are revealed
			let visibleListData = this.state.listData.slice();
			if (value) {
				this.props.getItems(value)
					.then((items) => {
						if(items.length === 0){
							this.setState({
								isSearchEmpty: true,
								isLoading: false
							});
						} else {
							// TODO: This logic can be more efficient!
							// 1) hide all items not returned in the current search
							visibleListData = visibleListData.filter(dataItem => items.find(item => dataItem.value === item.value));
							// 2) add any new items to list data
							let listData = this.state.listData.slice();
							items.map(item => {
								const isNew = visibleListData.findIndex(dataItem => {
									return dataItem.value === item.value;
								}) === -1;
								if (isNew) {
									const newItem = this._getListItemFromQueryItem(item);
									visibleListData.push(newItem);
									listData.push(newItem);
								}
							});
							this.setState({
								listData: listData,
								visibleListData: visibleListData,
								isLoading: false,
								isSearchEmpty: false
							}, this._updateSelectAllValues);
						}
					}, (e) => {
						this.props.logger.error(e);
						this.setState({
							isError: true
						});
					});
			} else {
				this.setState({
					isLoading: false,
					visibleListData: visibleListData
				}, this._updateSelectAllValues);
			}
		}

		/**
		 * Clears the current sort specification.
		 */
		_removeAll() {
			this.sortRemainingItemsBy = 'auto';
			let listData = this.state.listData.slice();
			listData.forEach(dataItem => dataItem.selected = false);
			// The only way this would not be clean is if there were previously items in the list.
			const isClean = this.state.previousSortableData.length === 0;
			this.setState({
				listData: listData.slice(),
				visibleListData: this.state.visibleListData ? this.state.visibleListData : listData,
				selectAllValues: false,
				btnDisabled: true,
				isClean: isClean,
				sortableData: []
			});
		}

		_itemRenderer(index, props) {
			const { ...other } = props;
			const item = this.state.visibleListData[index];
			return (
				<ListItem {...other} key={item.value} data-index={index} className='customSortCheckboxItem'>
					<Checkbox
						label={item.label}
						checked={item.selected}
						onChange={this._columnSelected.bind(this, item.value, false)}
					/>
				</ListItem>
			);
		}

		/**
		 * Performs a selection on all the visible values
		 * @param {object} evt
		 */
		_selectAll(evt) {
			// change all the visible values to the isChecked value
			const isChecked = !!evt.target.checked;
			const visibleDataToChange = this.state.visibleListData.slice().filter(dataItem => (dataItem.selected !== isChecked));
			visibleDataToChange.forEach(dataItem => this._columnSelected(dataItem.value, false));

			//enable the apply, clear all and sort remaining by items only if we have something checked, if not, disable it
			this.setState({
				selectAllValues: true,
				btnDisabled: this.state.sortableData.length === 0,
				isClean: this._isClean()
			});
		}

		/**
		 * Performs a selection on the given item
		 * @param {string} itemValue value of the selected item
		 */
		_columnSelected(itemValue, initialLoad) {
			//copy the data in the state
			let visibleListData = this.state.visibleListData.slice();
			let selectedItems = this.state.sortableData;

			//filter the copied data on the item that has been selected
			visibleListData.find(dataItem => {
				if (dataItem.value === itemValue) {
					//toggle selection
					dataItem.selected = !dataItem.selected;
					//if the checkbox was checked, then add it to the sortableData list, otherwise remove it
					if (dataItem.selected) {
						selectedItems.push(dataItem);
					} else {
						const itemIndex = selectedItems.findIndex(selectedItem => selectedItem.value === dataItem.value);
						selectedItems.splice(itemIndex, 1);
					}
				}
			});

			//enable the apply, clear all and sort remaining by items only if we have something checked, if not, disable it
			this.setState({
				visibleListData: visibleListData,
				btnDisabled: selectedItems.length === 0,
				sortableData: selectedItems,
				isClean: initialLoad? true : this._isClean(selectedItems),
				previousSortableData: initialLoad? JSON.parse(JSON.stringify(selectedItems)) : this.state.previousSortableData
			}, this._updateSelectAllValues);
		}

		/**
		 * Callback function for the nest sortable list
		 * @param {array} currentSelection
		 * @param {string} value the changed selection
		 */
		onChange(currentSelection, value = '') {
			//If the user deletes an item from the custom sort list, uncheck that particular value from the list. In other cases, such as move to top and drag and drop, no action needs to be taken.
			if(currentSelection.length < this.state.sortableData.length){
				this._columnSelected(value);
			}

			//set state, so that the child component (sortableList) will get re-rendered
			this.setState({
				sortableData: currentSelection,
				isClean: this._isClean(currentSelection)
			}, this._updateSelectAllValues);
		}

		render() {
			let ListItems;
			if (!this.state.openDialog) {
				return null;
			}
			if (this.state.isError) {
				ListItems = <RenderEmptyState
					type={'error'}
					title={resources.get('somethingWentWrong')}
				/>;
			} else if (this.state.isSearchEmpty){
				ListItems = <RenderEmptyState
					type={'no_results'}
					title={resources.get('searchReturnedNoResults')}
				/>;
			} else {
				{
					ListItems = this.state.isLoading ?
						(<Container id='overlay'>
							<ProgressIndicator tabIndex={0} size='normal' />
						</Container>) :
						<div>
							<div className='selectAllCheckbox'>
								<Checkbox
									key='all'
									label={resources.get('selectAll')}
									checked={this.state.selectAllValues}
									onClick={(event) => this._selectAll(event)}
								/>
							</div>
							<Container className='customSortCheckboxList'>
								<List
									length={this.state.visibleListData.length}
									itemRenderer={this._itemRenderer.bind(this)}
									selectionMode='multiple'
									fullHeight='true'
								/>
							</Container>
						</div>;
				}
			}

			return(
				<div>
					<Dialog
						{...this.dialogProps}
						onClose={this._onClose.bind(this)}
					>
						<Dialog.Header>
							<TruncatedText
								value={resources.get('createCustomSortTitle', { 'name': this.props.label })}
								className='customSortColumnLabel'
							/>
							<TruncatedText value={resources.get('createCustomSort')}/>
						</Dialog.Header>
						<Dialog.Body>
							<Label className='customSortTitleLabel' label={resources.get('createCustomSortDesc')}/>
							<FlexLayout direction='row'>
								<FlexItem
									gutter={[0]}
									className='customSortItemsDialog'
								>
									<Label className='customSortLabel' label={resources.get('availableItems')}/>
									<Container
										id='avaliableItemsForSort'
										className ='customSortAvaliableItemsContainer'
									>
										<SearchInput
											size='sm'
											placeholder={resources.get('search')}
											value={this.state.searchTerm}
											id='searchAllItems'
											onChange={this._onSearch.bind(this)}
											delay={800}
											className='customSortItemSearch'
										/>
										{ListItems}
									</Container>
								</FlexItem>
								<FlexItem grow>
									<Label className='customSortLabel' label={resources.get('customSortOrder')}/>
									<Container
										className='customSortSelectedItemsContainer'
										id='customSortList'
									>
										<SortableList
											sortableData={this.state.sortableData}
											onChange={this.onChange.bind(this)}
											iconsFeature={this.state.iconsFeature}
										/>
										<div className='customSortRemoveAll'>
											<Tooltip title={resources.get('resetTooltip', { 'customSortOrder': resources.get('customSortOrder'), 'sortRemainingItemsBy': resources.get('sortRemainingItemsBy') })} orient="left">
												<Link
													id='removeAllLink'
													onClick={this._removeAll.bind(this)}
													disabled={this.state.btnDisabled}>
													{resources.get('removeAll')}
												</Link>
											</Tooltip>
										</div>
									</Container>
									<Label className='customSortOrderSelectLabel' label={resources.get('sortRemainingItemsBy')} disabled={this.state.btnDisabled}/>
									<div className='customSortOrderSelect'>
										<Select
											options={[{ value: 'asc', label: resources.get('toolbarActionSortAscending') },
												{ value: 'desc', label: resources.get('toolbarActionSortDescending') },
												{ value: 'auto', label: resources.get('toolbarActionSortAuto', { sortOrder: this.props.defaultSortOrder }) }] }
											id='selectSortRemainingItemsBy'
											selected={this.sortRemainingItemsBy}
											disabled={this.state.btnDisabled}
											onChange={(item) => this._onSelect(item)}
										/>
									</div>
								</FlexItem>
							</FlexLayout>
						</Dialog.Body>
						<Dialog.Footer>
							<Dialog.Button
								primary
								id='applyCustomSort'
								label={resources.get('dlg_create')}
								onClick={this._onApply.bind(this)}
								disabled={this.state.isClean}
							/>
							<Dialog.Button
								label={resources.get('dlg_cancel')}
								id='cancelCustomSort'
								onClick={this._onClose.bind(this)}
							/>
						</Dialog.Footer>
					</Dialog>
				</div>
			);

		}
	}

	class CustomSortDialog extends View {

		init(...args){
			const properties = args[0];
			super.init(...args);
			this.label = properties.label;
			this.getItems = properties.getItems;
			this.getItemsFromValues = properties.getItemsFromValues;
			this.sortSpec = properties.sortSpec;
			this.sortType = properties.sortType;
			this.defaultSortOrder = properties.defaultSortOrder;
			this.applyCustomSort = properties.apply;
			this.reactDomNode = this.$el[0];
			this.logger = properties.logger;
			this.iconsFeature = properties.iconsFeature;
		}

		/**
		 * Renders the dialog
		 */
		render() {
			ReactDOM.render(
				<ReactComponents
					label={this.label}
					openDialog={true}
					getItems={this.getItems.bind(this)}
					getItemsFromValues={this.getItemsFromValues.bind(this)}
					sortSpec={this.sortSpec}
					sortType={this.sortType}
					defaultSortOrder={this.defaultSortOrder}
					applyCustomSort={this.onApply.bind(this)}
					onRemove={this.onCancel.bind(this)}
					logger={this.logger}
					iconsFeature={this.iconsFeature}
				/>,
				this.reactDomNode
			);
		}

		/**
		 * Applies the currently selected custom sort
		 * @param {object} state
		 * @param {string} sortRemainingItemsBy
		 */
		onApply(state, sortRemainingItemsBy) {
			//get the current Custom Sort Order list from the state that we store.
			let sortDataItems = state.sortableData;
			let topSpec = [];
			sortDataItems.forEach((sortData) => {
				topSpec.push(sortData.value);
			});

			//create the spec needed to be consumed by the API so that the sort can be applied to the viz
			const customSortSpec = {
				top: topSpec,
				type: sortRemainingItemsBy
			};

			//Apply custom sort
			this.applyCustomSort(customSortSpec);
			this.onCancel();
		}

		/**
		 * Cancels the current custom sort editing operation
		 */
		onCancel() {
			if (this.reactDomNode) {
				ReactDOM.unmountComponentAtNode(this.reactDomNode);
			}
			this.$el.remove();
			this.$el = null;
			CustomSortDialog.inherited('remove', this);
		}
	}

	return CustomSortDialog;
});
